import { LoadingButton } from "@mui/lab";
import {
  Button,
  Grid,
  Input,
  List,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import { FormikErrors } from "formik";
import React, { useState } from "react";
import { IMaskInput } from "react-imask";
import SignatureCanvas from "react-signature-canvas";
import { ApiError, ApiErrorClass, Profile } from "@readyfit-common/models";
import CoachPreviewModal from "src/grading/components/CoachPreviewModal/CoachPreviewModal";
import ImageCard from "src/grading/components/ImageCard/ImageCard";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { selectCurrentUser } from "src/store/reducers/auth/authSlice";
import { useUpdateProfileSignatureMutation } from "src/store/reducers/documents/documentsApiSlice";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import useStyles from "./Profile.styles";

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskCustom = React.forwardRef<HTMLInputElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        mask="+0 (000) 000-0000"
        definitions={{
          "#": /[1-9]/,
        }}
        inputRef={ref}
        onAccept={(value: any) =>
          onChange({ target: { name: props.name, value } })
        }
        overwrite
      />
    );
  },
);

const ProfileInfoRow = ({
  label,
  value,
  isEditing,
  handleChange,
  field,
  fieldType,
  errors,
}: {
  label: string;
  value: string;
  isEditing: boolean;
  fieldType?: "number" | "text" | "textarea" | "list" | "phoneNumber";
  handleChange: (
    field: string,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  field: string;
  errors: FormikErrors<{ [key: string]: string }>;
}) => {
  const classes = useStyles({ isEditing })();
  const handleNumberChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
  ) => {
    const value = event.target.value;
    // Only update if the value is a valid number or empty
    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
      handleChange(field)(event as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <Grid container className={classes.profileRow}>
      <Grid item xs={6}>
        <Typography variant="caption">{label}</Typography>
      </Grid>
      <Grid container item xs={6} justifyContent={"flex-end"}>
        {isEditing ? (
          fieldType === "phoneNumber" ? (
            <Input
              value={value}
              onChange={handleChange(field)}
              name={field}
              id="formatted-text-mask-input"
              inputComponent={TextMaskCustom as any}
            />
          ) : (
            <TextField
              name={field}
              variant="standard"
              value={value === "-" ? "" : value}
              onChange={
                fieldType === "number"
                  ? (event) => handleNumberChange(event, field)
                  : handleChange(field)
              }
              error={!!errors[field]}
              helperText={errors[field]}
              {...(fieldType === "textarea"
                ? {
                    multiline: true,
                    minRows: 2,
                  }
                : {})}
            />
          )
        ) : fieldType === "list" ? (
          <List dense disablePadding sx={{ marginTop: 0 }}>
            {value.split(",").map((item, index) => (
              <ListItem key={index} disableGutters>
                <Typography variant="caption">{item.trim()}</Typography>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="caption">{value}</Typography>
        )}
      </Grid>
    </Grid>
  );
};

interface ProfileTabProps {
  setIsEditing: (value: boolean) => void;
  isEditing: boolean;
  isLoadingUpdateProfile: boolean;
  profilePhoto: string;
  backgroundPhoto: string;
  handleOnProfilePhotoSelect: (file: File) => void;
  handleOnBackgroundPhotoSelect: (file: File) => void;
  profileInfoRows: {
    label: string;
    value: string | undefined;
    field: string;
    fieldType: string;
  }[];
  values: { [key: string]: string };
  errors: FormikErrors<{ [key: string]: string }>;
  handleEditClick: () => void;
  handleChange: (
    field: string,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  canvasRef: React.RefObject<SignatureCanvas>;
  showExampleModal: boolean;
  setShowExampleModal: (value: boolean) => void;
}

const ProfileTab = ({
  setIsEditing,
  isEditing,
  isLoadingUpdateProfile,
  profilePhoto,
  backgroundPhoto,
  handleOnProfilePhotoSelect,
  handleOnBackgroundPhotoSelect,
  profileInfoRows,
  values,
  errors,
  handleEditClick,
  handleChange,
  canvasRef,
  showExampleModal,
  setShowExampleModal,
}: ProfileTabProps) => {
  const classes = useStyles({ isEditing })();
  const [signatureInitiated, setSignatureInitiated] = useState<boolean>(false);
  const [updateProfileSignatureMutation] = useUpdateProfileSignatureMutation();
  const dispatch = useAppDispatch();
  const profile: Profile = useAppSelector(selectCurrentUser);
  const [newSignature, setNewSignature] = useState<boolean>(false);

  const handleClearSignature = () => {
    canvasRef.current?.clear();
    setSignatureInitiated(false);
    setNewSignature(true);
  };

  const handleSaveSignature = async () => {
    if (canvasRef?.current) {
      try {
        const dataUrl = canvasRef.current.toDataURL();
        const response = await fetch(dataUrl);
        // send blob to server
        const blob = await response.blob();
        const formData = new FormData();
        formData.append("signature", blob);
        const signatureResponse =
          await updateProfileSignatureMutation(formData).unwrap();

        if (!signatureResponse.success)
          throw new ApiErrorClass({
            message: "Error updating profile",
          });

        dispatch(
          showSnackbar({
            open: true,
            message: signatureResponse.message,
            type: "success",
          }),
        );
        setNewSignature(false);
        setSignatureInitiated(false);
      } catch (error: unknown) {
        const typedError = error as ApiError;
        dispatch(
          showSnackbar({
            open: true,
            message: typedError.data?.message || "Error updating profile",
            type: "error",
          }),
        );
      }
    }
  };

  return (
    <Grid container className={classes.contentContainer}>
      <Grid container alignItems={"center"}>
        <Typography variant="h4" fontWeight={"bold"}>
          Personal Information
        </Typography>

        <Grid item ml="auto" flexDirection={"row"}>
          <Grid container columnGap={1}>
            {isEditing && (
              <Button
                variant="contained"
                className={classes.button}
                onClick={() => setIsEditing(false)}
                type="submit"
                form="profileForm"
              >
                Cancel
              </Button>
            )}

            <LoadingButton
              variant="contained"
              className={isEditing ? classes.editButton : classes.button}
              type="submit"
              loading={isLoadingUpdateProfile}
              form="profileForm"
            >
              {isEditing ? "Save" : "Edit"}
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>

      <Grid container className={classes.profileColumnContainer} spacing={5}>
        {/* FIRST COLUMN */}
        <Grid item xs={6}>
          <Grid container className={classes.profileRow}>
            <Grid item xs={6}>
              <Typography variant="caption">Profile photo</Typography>
            </Grid>
            <Grid container item xs={6} justifyContent={"flex-end"}>
              <ImageCard
                src={profilePhoto}
                onFileSelect={handleOnProfilePhotoSelect}
              />
            </Grid>
          </Grid>

          <Grid container className={classes.profileRow}>
            <Grid item xs={6}>
              <Typography variant="caption">Background photo</Typography>
            </Grid>
            <Grid
              container
              item
              xs={6}
              flexDirection={"row"}
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              <Grid item>
                <Button
                  sx={{ color: "black" }}
                  onClick={() => setShowExampleModal(true)}
                >
                  See example
                </Button>
              </Grid>
              <ImageCard
                imageContainerStyles={classes.backgroundImageContainer}
                src={backgroundPhoto}
                onFileSelect={handleOnBackgroundPhotoSelect}
              />
            </Grid>
          </Grid>

          <Grid container marginTop={3}>
            <Grid item xs={6}>
              <Typography variant="caption">Signature</Typography>
            </Grid>
            <Grid
              container
              item
              xs={6}
              justifyContent={"flex-end"}
              flexDirection={"row"}
            >
              {signatureInitiated && (
                <Button onClick={handleSaveSignature}>Save</Button>
              )}

              <Button onClick={handleClearSignature}>Change Signature</Button>
            </Grid>
            <Grid container item>
              {profile?.signature && !newSignature ? (
                <img src={profile?.signature} alt="Signature" />
              ) : (
                <div
                  style={{
                    height: "200px",
                    width: "100%",
                    position: "relative",
                  }}
                >
                  <SignatureCanvas
                    ref={canvasRef}
                    penColor="black"
                    canvasProps={{
                      className: classes.signatureCanvas,
                    }}
                    onEnd={() => {
                      setSignatureInitiated(true);
                    }}
                  />
                </div>
              )}
            </Grid>
          </Grid>
        </Grid>
        {/* SECOND COLUMN */}
        <Grid
          item
          xs={6}
          component={"form"}
          id="profileForm"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleEditClick();
          }}
        >
          {profileInfoRows.map((row, index) => {
            return (
              <ProfileInfoRow
                key={index}
                label={row.label}
                value={values[row.field] || "-"}
                isEditing={isEditing}
                handleChange={(field) => handleChange(field)}
                field={row.field}
                fieldType={
                  row.fieldType as "number" | "text" | "textarea" | "list"
                }
                errors={errors}
              />
            );
          })}
        </Grid>
      </Grid>
      <CoachPreviewModal
        open={showExampleModal}
        onClose={() => setShowExampleModal(false)}
      />
    </Grid>
  );
};

export default ProfileTab;
